// Width / height ratios. Must match ASPECT_RATIOS in api/moodboard_render.py.
export const ASPECTS = {
  landscape: { label: "Landscape", detail: "16:9", ratio: 16 / 9 },
  "a4-landscape": { label: "A4 landscape", detail: "297 × 210", ratio: 297 / 210 },
  square: { label: "Square", detail: "1:1", ratio: 1 },
  portrait: { label: "A4 portrait", detail: "210 × 297", ratio: 210 / 297 },
};

export const aspectRatio = (aspect) => (ASPECTS[aspect] || ASPECTS.landscape).ratio;

export const BACKGROUNDS = ["#ffffff", "#f7f3ec", "#efe9dd", "#e6ded0", "#2f2a24", "#1e1b16"];

const SWATCH_COLOURS = ["#e9e1d3", "#c9b79c", "#b8915a", "#7a5c3e", "#2f2a24"];

export const uid = () =>
  window.crypto?.randomUUID?.() ||
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const COMMON = { opacity: 1, locked: false };

export const makeImage = (box, extra = {}) => ({
  id: uid(),
  type: "image",
  source: null,
  ref: null,
  fit: "cover",
  focus_x: 50,
  focus_y: 50,
  zoom: 1,
  radius: 0,
  border_width: 0,
  border_color: "#ffffff",
  caption: false,
  ...COMMON,
  ...box,
  ...extra,
});

export const makeText = (box, extra = {}) => ({
  id: uid(),
  type: "text",
  text: "Add your text",
  font_size: 2.4,
  font_family: "sans",
  font_weight: "normal",
  color: "#1e1b16",
  align: "left",
  ...COMMON,
  ...box,
  ...extra,
});

export const makeSwatch = (box, extra = {}) => ({
  id: uid(),
  type: "swatch",
  color: "#b8915a",
  label: "",
  radius: 0,
  ...COMMON,
  ...box,
  ...extra,
});

export const DEFAULT_STROKE = "#a8563c";

// Pencil, arrow and box items; box and points come from boxFromPoints().
// Only the fields each kind of drawing uses are copied from the tool style.
const pick = (style, keys) => Object.fromEntries(keys.filter((key) => key in style).map((key) => [key, style[key]]));

export const makeDrawing = (shape, style = {}) => ({
  id: uid(),
  type: "draw",
  brush: "pencil",
  stroke: DEFAULT_STROKE,
  stroke_width: 0.25,
  dash: "solid",
  ...COMMON,
  ...pick(style, ["brush", "stroke", "stroke_width", "dash"]),
  ...shape,
});

export const makeArrow = (shape, style = {}) => ({
  id: uid(),
  type: "arrow",
  route: "straight",
  bend: 0.35,
  head_start: "none",
  head_end: "arrow",
  stroke: DEFAULT_STROKE,
  stroke_width: 0.35,
  dash: "solid",
  ...COMMON,
  ...pick(style, ["route", "bend", "head_start", "head_end", "stroke", "stroke_width", "dash"]),
  ...shape,
});

export const makeShape = (box, style = {}) => ({
  id: uid(),
  type: "shape",
  shape: "rect",
  stroke: DEFAULT_STROKE,
  stroke_width: 0.35,
  fill: null,
  radius: 0,
  dash: "solid",
  ...COMMON,
  ...pick(style, ["shape", "stroke", "stroke_width", "fill", "radius", "dash"]),
  ...box,
});

// Starting styles for the three drawing tools; the editor remembers changes.
export const TOOL_DEFAULTS = {
  pencil: { brush: "pencil", stroke: DEFAULT_STROKE, stroke_width: 0.25, dash: "solid" },
  shape: { shape: "rect", stroke: DEFAULT_STROKE, stroke_width: 0.35, fill: null, radius: 0, dash: "solid" },
  arrow: { route: "straight", bend: 0.35, head_start: "none", head_end: "arrow", stroke: DEFAULT_STROKE, stroke_width: 0.35, dash: "solid" },
};

// Margin and gap are in percent of the board width; the vertical versions are
// scaled by the aspect ratio so spacing looks the same in both directions.
const MARGIN = 3;
const GAP = 1.4;

function layout(spec, aspect) {
  const ratio = aspectRatio(aspect);
  const mx = MARGIN;
  const my = MARGIN * ratio;
  const gx = GAP;
  const gy = GAP * ratio;

  const track = (sizes, margin, gap) => {
    const total = sizes.reduce((sum, size) => sum + size, 0);
    const free = 100 - 2 * margin - (sizes.length - 1) * gap;
    const starts = [];
    let cursor = margin;
    sizes.forEach((size) => {
      starts.push(cursor);
      cursor += (size / total) * free + gap;
    });
    return {
      start: (index) => starts[index],
      span: (index, count) =>
        sizes.slice(index, index + count).reduce((sum, size) => sum + (size / total) * free, 0) +
        (count - 1) * gap,
    };
  };

  const cols = track(spec.cols, mx, gx);
  const rows = track(spec.rows, my, gy);
  let swatchIndex = 0;

  return spec.cells.map(([col, row, colSpan = 1, rowSpan = 1, type = "image", extra = {}], index) => {
    const box = {
      x: round(cols.start(col)),
      y: round(rows.start(row)),
      w: round(cols.span(col, colSpan)),
      h: round(rows.span(row, rowSpan)),
      z: index + 1,
    };
    if (type === "text") return makeText(box, extra);
    if (type === "swatch") {
      return makeSwatch(box, { color: SWATCH_COLOURS[swatchIndex++ % SWATCH_COLOURS.length], ...extra });
    }
    return makeImage(box, extra);
  });
}

const round = (value) => Math.round(value * 1000) / 1000;

const TITLE = { text: "Board title", font_family: "serif", font_size: 3.2 };

const SPECS = {
  blank: { label: "Blank", cols: [1], rows: [1], cells: [] },
  grid: {
    label: "Grid",
    cols: [1, 1, 1],
    rows: [1, 1],
    cells: [[0, 0], [1, 0], [2, 0], [0, 1], [1, 1], [2, 1]],
  },
  hero: {
    label: "Hero",
    cols: [2, 1, 1],
    rows: [1, 1],
    cells: [[0, 0, 1, 2], [1, 0], [2, 0], [1, 1], [2, 1]],
  },
  editorial: {
    label: "Editorial",
    cols: [1, 1, 1, 1],
    rows: [0.55, 3, 0.9],
    cells: [
      [0, 0, 4, 1, "text", TITLE],
      [0, 1, 2, 1],
      [2, 1],
      [3, 1],
      [0, 2, 1, 1, "swatch"],
      [1, 2, 1, 1, "swatch"],
      [2, 2, 1, 1, "swatch"],
      [3, 2, 1, 1, "swatch"],
    ],
  },
  collage: {
    label: "Collage",
    cols: [1, 1, 1, 1],
    rows: [1, 1, 1],
    cells: [[0, 0, 2, 2], [2, 0], [3, 0, 1, 2], [2, 1, 1, 2], [0, 2], [1, 2], [3, 2]],
  },
  palette: {
    label: "Feature + palette",
    cols: [3, 1],
    rows: [1, 1, 1, 1, 1],
    cells: [
      [0, 0, 1, 5],
      [1, 0, 1, 1, "swatch"],
      [1, 1, 1, 1, "swatch"],
      [1, 2, 1, 1, "swatch"],
      [1, 3, 1, 1, "swatch"],
      [1, 4, 1, 1, "swatch"],
    ],
  },
  strip: {
    label: "Strip",
    cols: [1, 1, 1, 1],
    rows: [1],
    cells: [[0, 0], [1, 0], [2, 0], [3, 0]],
  },
  mosaic: {
    label: "Mosaic",
    cols: [1, 1, 1],
    rows: [1, 1, 1],
    cells: [[0, 0], [1, 0], [2, 0], [0, 1], [1, 1], [2, 1], [0, 2], [1, 2], [2, 2]],
  },
  grid12: {
    label: "Grid of 12",
    cols: [1, 1, 1, 1],
    rows: [1, 1, 1],
    cells: Array.from({ length: 12 }, (_, index) => [index % 4, Math.floor(index / 4)]),
  },
  grid15: {
    label: "Grid of 15",
    cols: [1, 1, 1, 1, 1],
    rows: [1, 1, 1],
    cells: Array.from({ length: 15 }, (_, index) => [index % 5, Math.floor(index / 5)]),
  },
};

export const TEMPLATES = Object.entries(SPECS).map(([key, spec]) => ({ key, label: spec.label }));

export const buildTemplate = (key, aspect) => layout(SPECS[key] || SPECS.blank, aspect);

// Re-flow an existing board into a template, keeping its images in reading order.
export function applyTemplate(key, aspect, currentItems) {
  const next = buildTemplate(key, aspect);
  const images = currentItems
    .filter((item) => item.type === "image" && item.source)
    .sort((a, b) => a.y - b.y || a.x - b.x);

  // The picture's own settings travel with it; the frame's turn does not.
  const carried = [
    "source", "ref", "fit", "focus_x", "focus_y", "zoom", "radius", "border_width", "border_color", "caption", "opacity",
    "crop", "flip_x", "flip_y", "brightness", "contrast", "saturation", "sepia",
  ];
  let cursor = 0;
  const filled = next.map((item) => {
    if (item.type !== "image" || cursor >= images.length) return item;
    const image = images[cursor++];
    return { ...item, ...Object.fromEntries(carried.filter((key) => key in image).map((key) => [key, image[key]])) };
  });
  return { items: filled, dropped: images.length - cursor };
}
